import React, { useState, useMemo, useEffect } from "react";

/** Preset colors: gradient from light to dark (15 colors) */
const PRESET_COLORS = [
  { name: "Snow", color: "#f8fafc" },
  { name: "Pearl", color: "#e2e8f0" },
  { name: "Silver", color: "#cbd5e1" },
  { name: "Mist", color: "#94a3b8" },
  { name: "Stone", color: "#64748b" },
  { name: "Coral", color: "#f97316" },
  { name: "Teal", color: "#0d9488" },
  { name: "Blue", color: "#2563eb" },
  { name: "Violet", color: "#7c3aed" },
  { name: "Rose", color: "#e11d48" },
  { name: "Slate", color: "#475569" },
  { name: "Graphite", color: "#334155" },
  { name: "Charcoal", color: "#1e293b" },
  { name: "Ink", color: "#0f172a" },
  { name: "Black", color: "#020617" },
];

function isPresetColor(hex, presets = PRESET_COLORS) {
  if (!hex) return false;
  const h = hex.replace(/^#/, "").toLowerCase();
  return presets.some((p) => (p.color || p).replace(/^#/, "").toLowerCase() === h);
}

const ColorPicker = ({
  label,
  colors = PRESET_COLORS,
  selectedColor,
  setSelectedColor,
  inputName,
  disabled = false,
}) => {
  const normalizedSelected = selectedColor && selectedColor.startsWith("#") ? selectedColor : selectedColor ? `#${String(selectedColor).replace(/^#/, "")}` : "#FF6A59";
  const [mode, setMode] = useState("preset");
  const [customHex, setCustomHex] = useState("#FF6A59");

  useEffect(() => {
    if (!normalizedSelected) return;
    if (isPresetColor(normalizedSelected, colors)) {
      setMode("preset");
    } else {
      setMode("custom");
      setCustomHex(normalizedSelected);
    }
  }, [normalizedSelected, colors]);

  const handlePresetSelect = (colorValue) => {
    const hex = colorValue.color || colorValue;
    setSelectedColor(hex);
    setMode("preset");
  };

  const handleCustomChange = (e) => {
    const hex = e.target.value;
    setCustomHex(hex);
    setSelectedColor(hex);
    setMode("custom");
  };

  const handleCustomHexInput = (e) => {
    let v = e.target.value.replace(/[^0-9A-Fa-f]/g, "");
    if (v.startsWith("#")) v = v.slice(1);
    if (v.length > 6) v = v.slice(0, 6);
    const hex = v ? `#${v}` : "#";
    setCustomHex(hex);
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) setSelectedColor(hex);
  };

  const switchToCustom = () => {
    setMode("custom");
    setCustomHex(normalizedSelected);
    setSelectedColor(normalizedSelected);
  };

  const switchToPreset = () => setMode("preset");

  const displayColor = mode === "custom" ? (customHex.startsWith("#") && customHex.length === 7 ? customHex : normalizedSelected) : normalizedSelected;

  return (
    <div className="settings-color-picker col-12 mb-4">
      <div className="settings-color-picker__card rounded-3 border shadow-sm overflow-hidden">
        <div
          className="settings-color-picker__preview px-3 py-3 text-white d-flex align-items-center justify-content-between"
          style={{
            background: `linear-gradient(135deg, ${displayColor} 0%, ${displayColor}dd 50%, ${displayColor}99 100%)`,
            minHeight: "56px",
          }}
        >
          <span className="fw-semibold small text-shadow">{label}</span>
          <span className="badge bg-dark bg-opacity-50 font-monospace">{displayColor}</span>
        </div>
        <div className="p-3 bg-light bg-opacity-50">
          <div className="btn-group btn-group-sm w-100 mb-3" role="group">
            <button
              type="button"
              className={`btn ${mode === "preset" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={switchToPreset}
              disabled={disabled}
            >
              <i className="fas fa-palette me-1" />
              Preset
            </button>
            <button
              type="button"
              className={`btn ${mode === "custom" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={switchToCustom}
              disabled={disabled}
            >
              <i className="fas fa-eyedropper me-1" />
              Custom
            </button>
          </div>

          {mode === "preset" ? (
            <div className="settings-color-picker__grid d-flex flex-wrap gap-2">
              {(colors || PRESET_COLORS).map((item, i) => {
                const colorValue = item.color || item;
                const colorId = `${inputName}_${i}_${(item.name || "").replace(/\s/g, "_")}`;
                const isSelected = (normalizedSelected || "").toLowerCase() === (colorValue || "").toLowerCase();
                return (
                  <label
                    key={colorId}
                    htmlFor={colorId}
                    className="settings-color-picker__swatch rounded-2 border-2 cursor-pointer d-flex align-items-center justify-content-center transition-all"
                    style={{
                      width: "36px",
                      height: "36px",
                      backgroundColor: colorValue,
                      borderColor: isSelected ? "var(--primary, #3250ff)" : "transparent",
                      boxShadow: isSelected ? "0 0 0 2px #fff, 0 0 0 4px var(--primary)" : "none",
                    }}
                  >
                    <input
                      type="radio"
                      name={inputName}
                      id={colorId}
                      value={colorValue}
                      checked={isSelected}
                      onChange={() => handlePresetSelect(item)}
                      disabled={disabled}
                      className="visually-hidden"
                    />
                    {isSelected && <i className="fas fa-check text-white small drop-shadow" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }} />}
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="color"
                  value={displayColor}
                  onChange={handleCustomChange}
                  disabled={disabled}
                  className="settings-color-picker__native rounded border cursor-pointer"
                  style={{ width: "44px", height: "44px", padding: "2px" }}
                />
                <input
                  type="text"
                  value={customHex}
                  onChange={handleCustomHexInput}
                  disabled={disabled}
                  placeholder="#000000"
                  className="form-control form-control-sm font-monospace"
                  style={{ width: "100px" }}
                  maxLength={7}
                />
              </div>
              <span className="small text-muted">Pick or type hex</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
export { PRESET_COLORS };
